import type { AssetSource, AssetEntry, LoadedAsset } from '@gotti/core';

const MIME: Record<string, string> = { json: 'application/json' };
function mimeFromExt(p: string): string {
  const ext = p.split('.').pop()?.toLowerCase() ?? '';
  return MIME[ext] ?? 'application/octet-stream';
}

function decodeImage(bytes: Uint8Array, mimeType: string): Promise<{ image: HTMLImageElement; url: string }> {
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ image, url });
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`HttpAssetSource: failed to decode image "${mimeType}".`)); };
    image.src = url;
  });
}

export class HttpAssetSource implements AssetSource {
  constructor(private readonly baseUrl: string) {}

  async load(path: string): Promise<LoadedAsset> {
    const res = await fetch(`${this.baseUrl}/${path}`);
    if (!res.ok) {
      throw new Error(`HttpAssetSource: ${res.status} loading "${path}".`);
    }
    const bytes = new Uint8Array(await res.arrayBuffer());
    const mimeType = res.headers.get('content-type') ?? mimeFromExt(path);
    if (mimeType.startsWith('image/') && typeof document !== 'undefined' && typeof URL !== 'undefined') {
      const { image, url } = await decodeImage(bytes, mimeType);
      return { path, mimeType, bytes, image, url };
    }
    return { path, mimeType, bytes };
  }

  // v1 is on-demand: no preload manifest over http.
  manifest(): AssetEntry[] {
    return [];
  }
}
